import { type CategoryKind, type SourceCategory } from '@zyx/shared';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useDeferredValue, useState } from 'react';
import { ErrorState } from '@/components/common/states';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { PersonPhoto } from '@/components/movie/person-photo';
import { useApi } from '@/hooks/use-connection';
import { useJobs, useStartJob } from '@/hooks/use-indexing';
import { formatCount } from '@/lib/format';
import { cn } from '@/lib/utils';

/** The tabs of the category picker, in the order the source's menu has them. */
const CATEGORY_TABS: ReadonlyArray<{ readonly kind: CategoryKind; readonly label: string }> = [
  { kind: 'listing', label: 'Listings' },
  { kind: 'genre', label: 'Genres' },
  { kind: 'maker', label: 'Makers' },
  { kind: 'series', label: 'Series' },
  { kind: 'actress', label: 'Actresses' },
  { kind: 'tag', label: 'Tags' },
];

const PAGE_SIZE = 100;

/** The range a category starts with: enough to try it, far from a whole category's tens of gigabytes. */
const DEFAULT_PAGES = 10;

/** Cover, hover clip, scene sprite and thumbnails: what one indexed movie takes on disk, on average. */
const BYTES_PER_MOVIE = 1.3e6;

function formatBytes(bytes: number): string {
  if (bytes >= 1e12) {
    return `${(bytes / 1e12).toFixed(1)} TB`;
  }
  if (bytes >= 1e9) {
    return `${(bytes / 1e9).toFixed(bytes >= 1e10 ? 0 : 1)} GB`;
  }
  return `${Math.max(1, Math.round(bytes / 1e6))} MB`;
}

/**
 * Starts a job. Mounted only while open, so every opening starts from the defaults.
 * `category` opens it on By category with that category chosen, as an actress's page does.
 */
export function NewJobDialog(props: { readonly open: boolean; readonly onOpenChange: (open: boolean) => void; readonly category?: SourceCategory }) {
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="max-w-2xl">{props.open ? <NewJobForm onDone={() => props.onOpenChange(false)} initial={props.category} /> : null}</DialogContent>
    </Dialog>
  );
}

type StartableKind = 'discover' | 'walk' | 'refresh' | 'categories';

const KINDS: ReadonlyArray<{ readonly kind: StartableKind; readonly label: string; readonly description: string }> = [
  { kind: 'discover', label: 'By category', description: 'Index the movies in a category of a source, over a range of its pages.' },
  { kind: 'walk', label: 'Random walk', description: 'Follow the links between movie pages in random order, indexing every movie it has not seen.' },
  { kind: 'refresh', label: 'Refresh', description: 'Bring indexed movies up to date: fetch old pages again, and check that old video streams still play.' },
  {
    kind: 'categories',
    label: 'Read categories',
    description: "Read the source's lists of genres, makers, series and actresses, with the actresses' photos: what By category offers. It also runs on its own once a week.",
  },
];

/** Source and kind, then the kind's own fields. By category and Random walk only add movies; Refresh updates them. */
function NewJobForm(props: { readonly onDone: () => void; readonly initial?: SourceCategory }) {
  const api = useApi();
  const sources = useQuery({ queryKey: ['sources'], queryFn: () => api.listSources() });
  const [sourceId, setSourceId] = useState<string | undefined>(props.initial?.sourceId);
  const [kind, setKind] = useState<StartableKind>('discover');
  const source = sources.data?.sources.find((candidate) => candidate.sourceId === sourceId) ?? sources.data?.sources[0];

  if (sources.isError) {
    return <ErrorState error={sources.error} />;
  }
  return (
    <div className="min-w-0 space-y-4">
      <DialogHeader>
        <DialogTitle>Start a job</DialogTitle>
        <DialogDescription>{KINDS.find((candidate) => candidate.kind === kind)?.description}</DialogDescription>
      </DialogHeader>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="job-source">Source</Label>
          <Select value={source?.sourceId ?? ''} onValueChange={setSourceId}>
            <SelectTrigger id="job-source">
              <SelectValue placeholder="Loading…" />
            </SelectTrigger>
            <SelectContent>
              {(sources.data?.sources ?? []).map((candidate) => (
                <SelectItem key={candidate.sourceId} value={candidate.sourceId}>
                  {candidate.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="job-kind">Kind</Label>
          <Select value={kind} onValueChange={(value) => setKind(KINDS.find((candidate) => candidate.kind === value)?.kind ?? 'discover')}>
            <SelectTrigger id="job-kind">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {KINDS.map((candidate) => (
                <SelectItem key={candidate.kind} value={candidate.kind}>
                  {candidate.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Keyed by source, so switching sources starts the fields over. */}
      {source === undefined ? null : kind === 'discover' ? (
        <CategoryJob key={source.sourceId} sourceId={source.sourceId} onDone={props.onDone} initial={props.initial?.sourceId === source.sourceId ? props.initial : undefined} />
      ) : kind === 'walk' ? (
        <WalkJob key={source.sourceId} sourceId={source.sourceId} sourceName={source.name} onDone={props.onDone} />
      ) : kind === 'refresh' ? (
        <RefreshJob key={source.sourceId} sourceId={source.sourceId} onDone={props.onDone} />
      ) : (
        <CategoriesJob key={source.sourceId} sourceId={source.sourceId} onDone={props.onDone} />
      )}
    </div>
  );
}

function StartButton(props: { readonly pending: boolean; readonly disabled: boolean }) {
  return (
    <DialogFooter>
      <Button type="submit" disabled={props.pending || props.disabled}>
        {props.pending ? 'Starting…' : 'Start'}
      </Button>
    </DialogFooter>
  );
}

/** A whole number from `min`, or undefined for an empty field; NaN for anything else. */
function wholeNumber(text: string, min: number): number | undefined {
  if (text.trim() === '') {
    return undefined;
  }
  const value = Number(text);
  return Number.isInteger(value) && value >= min ? value : Number.NaN;
}

/** A category, then a page range of it. */
function CategoryJob(props: { readonly sourceId: string; readonly onDone: () => void; readonly initial?: SourceCategory }) {
  const api = useApi();
  const start = useStartJob();
  const [category, setCategory] = useState<SourceCategory | undefined>(props.initial);
  // What the viewer typed; until then the range is the whole category.
  const [firstPageInput, setFirstPage] = useState<string | undefined>(undefined);
  const [lastPageInput, setLastPage] = useState<string | undefined>(undefined);

  const pages = useQuery({
    queryKey: ['category-pages', props.sourceId, category?.path],
    queryFn: () => api.getCategoryPages(props.sourceId, category?.path ?? ''),
    enabled: category !== undefined,
    staleTime: 10 * 60_000,
  });

  const firstPage = firstPageInput ?? '1';
  const lastPage = lastPageInput ?? (pages.data === undefined ? '' : String(Math.min(pages.data.lastPage, DEFAULT_PAGES)));
  const selectCategory = (next: SourceCategory | undefined): void => {
    setCategory(next);
    setFirstPage(undefined);
    setLastPage(undefined);
  };

  const first = Number(firstPage);
  const last = Number(lastPage);
  const max = pages.data?.lastPage;
  const rangeError =
    max === undefined
      ? undefined
      : !Number.isInteger(first) || !Number.isInteger(last) || first < 1 || last < 1
        ? 'Pages are whole numbers from 1.'
        : first > last
          ? 'The first page is after the last.'
          : last > max
            ? `This category has ${max.toLocaleString()} pages.`
            : undefined;
  const movies = pages.data === undefined || rangeError !== undefined ? undefined : (last - first + 1) * pages.data.moviesPerPage;

  return (
    <form
      className="min-w-0 space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (category === undefined || movies === undefined) {
          return;
        }
        start.mutate({ kind: 'discover', sourceId: props.sourceId, params: { lists: [category.path], firstPage: first, maxPages: last } }, { onSuccess: props.onDone });
      }}
    >
      <CategoryPicker sourceId={props.sourceId} selected={category} onSelect={selectCategory} initial={props.initial} />

      {category === undefined ? null : pages.isPending ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Counting the pages of {category.name}…
        </p>
      ) : pages.isError ? (
        <ErrorState error={pages.error} onRetry={() => void pages.refetch()} />
      ) : (
        <div className="space-y-3 rounded-lg border border-border p-3">
          <p className="text-sm">
            <span className="font-medium">{category.name}</span> has <span className="tabular-nums">{pages.data.lastPage.toLocaleString()}</span> page
            {pages.data.lastPage === 1 ? '' : 's'} of {pages.data.moviesPerPage} movies.
            {pages.data.pageLimit !== undefined && pages.data.lastPage >= pages.data.pageLimit ? (
              <span className="text-muted-foreground"> The source shows no further than page {pages.data.pageLimit.toLocaleString()}, so older movies in it are out of reach here.</span>
            ) : null}
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="job-first">From page</Label>
              <Input id="job-first" type="number" min={1} max={max} value={firstPage} onChange={(event) => setFirstPage(event.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="job-last">To page</Label>
              <Input id="job-last" type="number" min={1} max={max} value={lastPage} onChange={(event) => setLastPage(event.target.value)} />
            </div>
          </div>
          {rangeError !== undefined ? (
            <p className="text-sm text-destructive">{rangeError}</p>
          ) : movies === undefined ? null : (
            <p className="text-sm text-muted-foreground">
              Up to {movies.toLocaleString()} movies, about {formatBytes(movies * BYTES_PER_MOVIE)} of covers, clips and scene images. Movies already in the library are not fetched again.
            </p>
          )}
        </div>
      )}

      <StartButton pending={start.isPending} disabled={movies === undefined} />
    </form>
  );
}

/** Where to start, and how many pages to fetch at most. */
function WalkJob(props: { readonly sourceId: string; readonly sourceName: string; readonly onDone: () => void }) {
  const start = useStartJob();
  const [startUrl, setStartUrl] = useState('');
  const [limitInput, setLimit] = useState('');
  const limit = wholeNumber(limitInput, 1);
  const limitError = Number.isNaN(limit) ? 'The limit is a whole number of pages from 1, or empty for none.' : undefined;

  return (
    <form
      className="min-w-0 space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (limitError !== undefined) {
          return;
        }
        start.mutate({ kind: 'walk', sourceId: props.sourceId, params: { startUrl: startUrl.trim() === '' ? undefined : startUrl.trim(), maxPages: limit } }, { onSuccess: props.onDone });
      }}
    >
      <div className="space-y-1.5">
        <Label htmlFor="walk-start">Start from</Label>
        <Input id="walk-start" value={startUrl} onChange={(event) => setStartUrl(event.target.value)} placeholder={`A movie page on ${props.sourceName}, or empty for its home page`} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="walk-limit">Page limit</Label>
        <Input id="walk-limit" type="number" min={1} value={limitInput} onChange={(event) => setLimit(event.target.value)} placeholder="No limit" className="w-40" />
        {limitError === undefined ? null : <p className="text-sm text-destructive">{limitError}</p>}
      </div>
      <p className="text-sm text-muted-foreground">
        It visits each page once, in random order. A movie already in the library is passed through without being fetched again, so only fetched pages count toward the limit (a movie saved before
        walks existed is fetched once, to learn its links). Without a limit it runs until no unvisited page is left, or until you cancel it. It runs behind any By-category job.
      </p>
      <StartButton pending={start.isPending} disabled={limitError !== undefined} />
    </form>
  );
}

/** One line of a refresh: a switch, a threshold in days, and how much it takes. */
function Threshold(props: {
  readonly id: string;
  readonly enabled: boolean;
  readonly onEnabled: (enabled: boolean) => void;
  readonly days: string;
  readonly onDays: (days: string) => void;
  readonly label: string;
  readonly noun: string;
  readonly hint: string;
  readonly count?: number;
}) {
  return (
    <div className="space-y-1.5 rounded-lg border border-border p-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Switch id={props.id} checked={props.enabled} onCheckedChange={props.onEnabled} aria-label={props.label} />
        <Label htmlFor={props.id} className="font-medium">
          {props.label}
        </Label>
        <span className="text-muted-foreground">last done more than</span>
        <Input type="number" min={0} value={props.days} onChange={(event) => props.onDays(event.target.value)} disabled={!props.enabled} className="h-8 w-20" aria-label={`${props.label}: days`} />
        <span className="text-muted-foreground">days ago</span>
      </div>
      <p className="text-xs text-muted-foreground">{props.hint}</p>
      {props.enabled && props.count !== undefined ? (
        <p className="text-sm tabular-nums">
          {props.count.toLocaleString()} {props.noun}
          {props.count === 1 ? '' : 's'}
        </p>
      ) : null}
    </div>
  );
}

/** Two thresholds, each with a switch, and what each would take before it starts. */
function RefreshJob(props: { readonly sourceId: string; readonly onDone: () => void }) {
  const api = useApi();
  const start = useStartJob();
  const [pagesOn, setPagesOn] = useState(true);
  const [pagesInput, setPages] = useState('30');
  const [streamsOn, setStreamsOn] = useState(true);
  const [streamsInput, setStreams] = useState('7');
  const pagesDays = pagesOn ? wholeNumber(pagesInput, 0) : undefined;
  const streamsDays = streamsOn ? wholeNumber(streamsInput, 0) : undefined;
  const invalid = (!pagesOn && !streamsOn) || Number.isNaN(pagesDays) || Number.isNaN(streamsDays) || (pagesOn && pagesDays === undefined) || (streamsOn && streamsDays === undefined);
  // Deferred, so typing a number does not count on every keystroke.
  const askedPages = useDeferredValue(pagesDays);
  const askedStreams = useDeferredValue(streamsDays);
  const preview = useQuery({
    queryKey: ['refresh-preview', props.sourceId, askedPages, askedStreams],
    queryFn: () => api.previewRefresh({ sourceId: props.sourceId, pagesOlderThanDays: askedPages, streamsOlderThanDays: askedStreams }),
    enabled: !invalid && !Number.isNaN(askedPages) && !Number.isNaN(askedStreams),
    placeholderData: keepPreviousData,
  });
  const total = (preview.data?.pages ?? 0) + (preview.data?.streams ?? 0);

  return (
    <form
      className="min-w-0 space-y-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (invalid) {
          return;
        }
        start.mutate({ kind: 'refresh', sourceId: props.sourceId, params: { pagesOlderThanDays: pagesDays, streamsOlderThanDays: streamsDays } }, { onSuccess: props.onDone });
      }}
    >
      <Threshold
        id="refresh-pages"
        enabled={pagesOn}
        onEnabled={setPagesOn}
        days={pagesInput}
        onDays={setPages}
        label="Refresh pages"
        noun="page"
        hint="Fetches each page again and updates its title, cast, genres, counts and episodes. A page the source no longer has is marked gone."
        count={preview.isPlaceholderData ? undefined : preview.data?.pages}
      />
      <Threshold
        id="refresh-streams"
        enabled={streamsOn}
        onEnabled={setStreamsOn}
        days={streamsInput}
        onDays={setStreams}
        label="Check video streams"
        noun="stream"
        hint="Confirms each video still plays. Streams that were not playing are always included."
        count={preview.isPlaceholderData ? undefined : preview.data?.streams}
      />
      {invalid ? (
        <p className="text-sm text-destructive">{!pagesOn && !streamsOn ? 'Turn on at least one.' : 'Days are whole numbers from 0; 0 takes everything.'}</p>
      ) : preview.isError ? (
        <ErrorState error={preview.error} onRetry={() => void preview.refetch()} />
      ) : (
        <p className="text-sm text-muted-foreground">Oldest first, behind any By-category job. Files that never downloaded are tried again along the way.</p>
      )}
      <StartButton pending={start.isPending} disabled={invalid || preview.data === undefined || preview.isPlaceholderData || total === 0} />
    </form>
  );
}

/** No fields: what it reads and when it last did, shown from the category counts. */
function CategoriesJob(props: { readonly sourceId: string; readonly onDone: () => void }) {
  const api = useApi();
  const start = useStartJob();
  const summary = useQuery({
    // Its own key: the picker's infinite query caches pages under ['categories', source, kind, search].
    queryKey: ['categories', props.sourceId, 'summary'],
    queryFn: () => api.listCategories({ sourceId: props.sourceId, kind: 'listing', limit: 1, offset: 0 }),
  });
  const counts = summary.data?.counts ?? {};
  // A paused read also blocks a new one; `refreshing` only covers one that is running.
  const jobs = useJobs();
  const unfinished = jobs.data?.jobs.find((job) => job.kind === 'categories' && job.sourceId === props.sourceId && ['pending', 'running', 'paused'].includes(job.status));
  return (
    <form
      className="min-w-0 space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        start.mutate({ kind: 'categories', sourceId: props.sourceId }, { onSuccess: props.onDone });
      }}
    >
      <p className="text-sm">
        {summary.data === undefined ? (
          'Counting what is known…'
        ) : (
          <>
            Known now:{' '}
            {CATEGORY_TABS.filter((tab) => tab.kind !== 'listing')
              .map((tab) => `${(counts[tab.kind] ?? 0).toLocaleString()} ${tab.label.toLowerCase()}`)
              .join(', ')}
            .
          </>
        )}
      </p>
      <p className="text-sm text-muted-foreground">
        It reads every page of the source's indexes (on 123av about 2,000 pages, mostly actresses) and downloads each actress photo it has not stored, behind any other job. Categories the source no
        longer lists are dropped by the weekly run.
      </p>
      {unfinished === undefined ? null : (
        <p className="text-sm text-muted-foreground">
          Job {unfinished.jobId} is already reading them and is {unfinished.status}
          {unfinished.status === 'paused' ? '; resume it under Indexing, or cancel it to start afresh' : ''}.
        </p>
      )}
      <StartButton pending={start.isPending} disabled={unfinished !== undefined || jobs.data === undefined} />
    </form>
  );
}

/** Tabs by kind, a search, and the categories most movies first, a hundred at a time. */
function CategoryPicker(props: { readonly sourceId: string; readonly selected?: SourceCategory; readonly onSelect: (category: SourceCategory) => void; readonly initial?: SourceCategory }) {
  const api = useApi();
  // Opened on a category: its tab, searched by its name, so it is in view and chosen.
  const [kind, setKind] = useState<CategoryKind>(props.initial?.kind ?? 'listing');
  const [query, setQuery] = useState(props.initial?.name ?? '');
  // Deferred, so typing stays responsive while results load.
  const search = useDeferredValue(query.trim());
  const categories = useInfiniteQuery({
    queryKey: ['categories', props.sourceId, kind, search],
    queryFn: ({ pageParam }) => api.listCategories({ sourceId: props.sourceId, kind, query: search, limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (last, all) => {
      const loaded = all.reduce((sum, page) => sum + page.categories.length, 0);
      return loaded < last.total ? loaded : undefined;
    },
    // While the source's indexes are being read, more keep arriving.
    refetchInterval: (state) => (state.state.data?.pages[0]?.refreshing === true ? 5_000 : false),
  });

  const first = categories.data?.pages[0];
  // By path: while a refresh adds rows, consecutive offset pages can overlap.
  const loaded = [...new Map((categories.data?.pages.flatMap((page) => page.categories) ?? []).map((category) => [category.path, category])).values()];
  const counts = first?.counts ?? {};
  const label = CATEGORY_TABS.find((tab) => tab.kind === kind)?.label ?? '';
  return (
    <div className="space-y-2">
      <Label htmlFor="job-category-search">Category</Label>
      <div className="flex flex-wrap gap-1" role="group" aria-label="Kind of category">
        {CATEGORY_TABS.map((tab) => (
          <Button key={tab.kind} type="button" size="sm" variant={tab.kind === kind ? 'secondary' : 'ghost'} aria-pressed={tab.kind === kind} onClick={() => setKind(tab.kind)}>
            {tab.label}
            <span className="tabular-nums text-muted-foreground">{formatCount(counts[tab.kind] ?? 0)}</span>
          </Button>
        ))}
      </div>
      <Input id="job-category-search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder={`Search ${label.toLowerCase()}`} />
      {first?.refreshing === true ? (
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="size-3 animate-spin" /> Still reading the source's category lists; more appear as they arrive.
        </p>
      ) : null}
      {categories.isError ? (
        <ErrorState error={categories.error} onRetry={() => void categories.refetch()} />
      ) : (
        <div className={cn('max-h-64 overflow-y-auto rounded-md border border-border', categories.isPending && 'opacity-60')}>
          <ul className="divide-y divide-border" role="listbox" aria-label={label}>
            {loaded.map((category) => {
              const selected = category.path === props.selected?.path;
              return (
                <li key={category.path} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={selected}
                    className={cn('flex w-full items-baseline gap-3 px-3 py-1.5 text-left text-sm hover:bg-muted', selected && 'bg-primary/10 font-medium')}
                    onClick={() => props.onSelect(category)}
                  >
                    {kind === 'actress' ? <PersonPhoto name={category.name} photo={category.photo} className="size-7 self-center text-xs" /> : null}
                    <span className="min-w-0 flex-1 truncate">{category.name}</span>
                    {category.movieCount === undefined ? null : <span className="shrink-0 tabular-nums text-xs text-muted-foreground">{category.movieCount.toLocaleString()} movies</span>}
                  </button>
                </li>
              );
            })}
          </ul>
          {first !== undefined && loaded.length === 0 ? <p className="px-3 py-2 text-sm text-muted-foreground">{search === '' ? 'None yet.' : 'Nothing matches.'}</p> : null}
          {categories.hasNextPage ? (
            <button
              type="button"
              className="w-full border-t border-border px-3 py-1.5 text-left text-sm text-primary hover:bg-muted disabled:opacity-60"
              disabled={categories.isFetchingNextPage}
              onClick={() => void categories.fetchNextPage()}
            >
              {categories.isFetchingNextPage ? 'Loading…' : `Show more (${((first?.total ?? 0) - loaded.length).toLocaleString()} left)`}
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}
